import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { usdToMinorUnits } from "../../shared/lib/usd.js";
import { Button } from "../../shared/ui/button.js";
import {
  eligibleFilteredSizingRiskBots,
  type FilteredSizingRiskSettings,
  useApplyFilteredSizingRisk,
} from "./filtered-sizing-risk-queries.js";
import type { BotView } from "./models.js";

interface FormValues extends FilteredSizingRiskSettings {
  readonly confirmed: boolean;
}

const positiveDecimal = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;

function sizingLabel(kind: FormValues["positionSizingKind"]): string {
  if (kind === "fixed-notional") return "Fixed USD notional";
  if (kind === "percent-of-equity") return "Percent of equity";
  return "Fixed quantity";
}

export function FilteredSizingRiskControls({ bots }: { readonly bots: readonly BotView[] }) {
  const [open, setOpen] = useState(false);
  const apply = useApplyFilteredSizingRisk();
  const eligibleBots = eligibleFilteredSizingRiskBots(bots);
  const skippedCount = bots.length - eligibleBots.length;
  const form = useForm<FormValues>({
    defaultValues: {
      allowShort: false,
      confirmed: false,
      maxPositionNotionalUsd: "",
      maxPositionQuantity: "",
      positionSizingKind: "fixed-quantity",
      positionSizingValue: "1",
    },
    mode: "onChange",
  });
  const sizingKind = useWatch({ control: form.control, name: "positionSizingKind" });
  const values = useWatch({ control: form.control });

  useEffect(() => {
    void form.trigger("positionSizingValue");
  }, [form, sizingKind]);

  const submit = form.handleSubmit(async (submitted) => {
    const settings: FilteredSizingRiskSettings = {
      allowShort: submitted.allowShort,
      maxPositionNotionalUsd: submitted.maxPositionNotionalUsd,
      maxPositionQuantity: submitted.maxPositionQuantity,
      positionSizingKind: submitted.positionSizingKind,
      positionSizingValue: submitted.positionSizingValue,
    };
    await apply.mutateAsync({ bots: eligibleBots, settings });
    form.setValue("confirmed", false);
  });
  const succeededCount =
    apply.data?.filter((outcome) => outcome.status === "succeeded").length ?? 0;
  const failed = apply.data?.filter((outcome) => outcome.status === "failed") ?? [];

  return (
    <div className="filtered-sizing-risk-launcher">
      <Button
        disabled={bots.length === 0 || apply.isPending}
        onClick={() => {
          apply.reset();
          form.setValue("confirmed", false);
          setOpen((current) => !current);
        }}
        tone="quiet"
      >
        {open ? "Close sizing controls" : "Apply filtered sizing/risk"}
      </Button>
      {open ? (
        <form
          aria-label="Apply position sizing and risk to filtered bots"
          className="form-stack filtered-sizing-risk-form"
          onSubmit={(event) => void submit(event)}
        >
          <div>
            <p className="eyebrow">New immutable revisions</p>
            <h2>Apply filtered sizing and risk</h2>
            <p className="section-description">
              Update {eligibleBots.length} editable {eligibleBots.length === 1 ? "bot" : "bots"}
              currently shown. Strategy, symbol, schedule, execution, name, and broker account
              remain unchanged.
              {skippedCount === 0
                ? ""
                : ` ${String(skippedCount)} ${skippedCount === 1 ? "bot is" : "bots are"} excluded because ${skippedCount === 1 ? "it" : "they"} cannot be edited in the current state.`}
            </p>
          </div>
          <div className="field-grid">
            <div className="field-stack">
              <label htmlFor="filtered-sizing-kind">Sizing method</label>
              <select id="filtered-sizing-kind" {...form.register("positionSizingKind")}>
                <option value="fixed-quantity">Fixed quantity</option>
                <option value="fixed-notional">Fixed USD notional</option>
                <option value="percent-of-equity">Percent of equity</option>
              </select>
            </div>
            <div className="field-stack">
              <label htmlFor="filtered-sizing-value">Sizing value</label>
              <input
                id="filtered-sizing-value"
                inputMode="decimal"
                {...form.register("positionSizingValue", {
                  validate: (value) => {
                    const parsed = Number(value);
                    if (
                      !positiveDecimal.test(value.trim()) ||
                      !Number.isFinite(parsed) ||
                      parsed <= 0
                    )
                      return "Enter a positive sizing value.";
                    if (sizingKind === "fixed-quantity" && !Number.isInteger(parsed))
                      return "Fixed quantity must be a whole number of shares.";
                    return true;
                  },
                })}
              />
              {form.formState.errors.positionSizingValue === undefined ? null : (
                <span className="field-error">
                  {form.formState.errors.positionSizingValue.message}
                </span>
              )}
            </div>
            <div className="field-stack">
              <label htmlFor="filtered-max-quantity">
                Maximum position quantity <span>(optional)</span>
              </label>
              <input
                id="filtered-max-quantity"
                inputMode="decimal"
                {...form.register("maxPositionQuantity", {
                  validate: (value) =>
                    value.trim() === "" ||
                    (positiveDecimal.test(value.trim()) && Number(value) > 0) ||
                    "Enter a positive quantity or leave it blank.",
                })}
              />
              {form.formState.errors.maxPositionQuantity === undefined ? null : (
                <span className="field-error">
                  {form.formState.errors.maxPositionQuantity.message}
                </span>
              )}
            </div>
            <div className="field-stack">
              <label htmlFor="filtered-max-notional">
                Maximum position notional (USD) <span>(optional)</span>
              </label>
              <input
                id="filtered-max-notional"
                inputMode="decimal"
                placeholder="6000"
                {...form.register("maxPositionNotionalUsd", {
                  validate: (value) => {
                    if (value.trim() === "") return true;
                    try {
                      usdToMinorUnits(value);
                      return true;
                    } catch (error) {
                      return error instanceof Error ? error.message : "Enter a valid USD amount.";
                    }
                  },
                })}
              />
              <span className="field-hint">Enter dollars, for example 6000 for $6,000.</span>
              {form.formState.errors.maxPositionNotionalUsd === undefined ? null : (
                <span className="field-error">
                  {form.formState.errors.maxPositionNotionalUsd.message}
                </span>
              )}
            </div>
          </div>
          <label className="check-field">
            <input type="checkbox" {...form.register("allowShort")} />
            <span>
              <strong>Allow short positions</strong>
              <small>
                This value replaces the current short-selling setting on every target bot.
              </small>
            </span>
          </label>
          <dl className="bulk-setting-preview" aria-label="Sizing and risk preview">
            <div>
              <dt>Sizing</dt>
              <dd>
                {sizingLabel(sizingKind)} ·{" "}
                {values.positionSizingValue === "" || values.positionSizingValue === undefined
                  ? "Not set"
                  : values.positionSizingValue}
              </dd>
            </div>
            <div>
              <dt>Maximum quantity</dt>
              <dd>
                {values.maxPositionQuantity === "" || values.maxPositionQuantity === undefined
                  ? "Not set"
                  : values.maxPositionQuantity}
              </dd>
            </div>
            <div>
              <dt>Maximum notional</dt>
              <dd>
                {values.maxPositionNotionalUsd === ""
                  ? "Not set"
                  : `$${values.maxPositionNotionalUsd}`}
              </dd>
            </div>
            <div>
              <dt>Short positions</dt>
              <dd>{values.allowShort ? "Allowed" : "Disabled"}</dd>
            </div>
          </dl>
          <label className="check-field">
            <input type="checkbox" {...form.register("confirmed", { required: true })} />
            <span>
              <strong>
                Apply these settings to {eligibleBots.length}{" "}
                {eligibleBots.length === 1 ? "bot" : "bots"}
              </strong>
              <small>Blank optional limits remove those limits from every target bot.</small>
            </span>
          </label>
          {apply.error === null ? null : (
            <p className="form-error" role="alert">
              The bulk update stopped unexpectedly. No additional bots will be submitted.
            </p>
          )}
          {apply.data === undefined ? null : (
            <div className="bulk-launch-result" role="status">
              <strong>
                Updated {succeededCount} of {apply.data.length} bots.
              </strong>
              {failed.length === 0 ? null : (
                <p>
                  Failed: {failed.map((outcome) => outcome.botName).join(", ")}. Refresh or open
                  each bot to review its current version and lifecycle state.
                </p>
              )}
            </div>
          )}
          <div className="button-row">
            <Button
              disabled={eligibleBots.length === 0 || apply.isPending || !form.formState.isValid}
              type="submit"
            >
              {apply.isPending
                ? "Applying…"
                : `Apply to ${String(eligibleBots.length)} ${eligibleBots.length === 1 ? "bot" : "bots"}`}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
